import { getErrorStatus } from '@/api/errors'
import { ScheduleFormDialog, WorkflowSchedulesTable } from '@/components/projects/schedules'
import { ProjectSectionHeader } from '@/components/projects/ProjectSectionHeader'
import { Button, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui'
import { Pagination } from '@/components/ui/pagination'
import { useAuth, usePagination, useWorkflowSchedules } from '@/hooks'
import { Permissions } from '@/lib/permissions'
import type { WorkflowScheduleListFilters, WorkflowScheduleState } from '@/types'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'

const ALL_STATES = 'all'

export function WorkflowSchedulesPage() {
  const { projectId } = useParams()
  const navigate = useNavigate()
  const { hasPermission, isLoading: authLoading } = useAuth(projectId)
  const [stateFilter, setStateFilter] = useState<WorkflowScheduleState | typeof ALL_STATES>(ALL_STATES)
  const [createOpen, setCreateOpen] = useState(false)
  const { page, pageSize, pageSizeOptions, setPage, setPageSize, resetPage, paginationParams } = usePagination({ defaultPageSize: 20 })

  const filters: WorkflowScheduleListFilters = stateFilter === ALL_STATES ? {} : { state: stateFilter }
  const { data, isLoading, isError, error, refetch } = useWorkflowSchedules(projectId!, filters, paginationParams)
  const schedules = data?.items ?? []
  const totalCount = data?.totalCount ?? 0

  const handleStateFilterChange = (value: string) => {
    if (!value) return
    setStateFilter(value as WorkflowScheduleState | typeof ALL_STATES)
    resetPage()
  }

  if (authLoading) return null
  if (!hasPermission(Permissions.Project.Schedule.View, projectId)) {
    return <Navigate to={`/projects/${projectId}/backlog`} replace />
  }

  const canCreate = hasPermission(Permissions.Project.Schedule.Create, projectId)

  return (
    <div className="space-y-6" data-testid="workflow-schedules-page">
      <ProjectSectionHeader sectionName="Schedules">
        {canCreate && (
          <Button onClick={() => setCreateOpen(true)} data-testid="new-schedule-button">
            <Plus className="h-4 w-4" />
            New schedule
          </Button>
        )}
      </ProjectSectionHeader>

      <div className="flex items-end gap-4">
        <div className="flex flex-col gap-1.5 w-48">
          <Label htmlFor="schedules-state-filter">State</Label>
          <Select value={stateFilter} onValueChange={handleStateFilterChange}>
            <SelectTrigger id="schedules-state-filter" aria-label="Filter schedules by state">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_STATES}>All states</SelectItem>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Paused">Paused</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {isError && getErrorStatus(error) === 403 ? (
        <div className="text-center py-12 text-muted-foreground" role="alert" data-testid="workflow-schedules-forbidden">
          You do not have permission to view schedules in this project.
        </div>
      ) : isError ? (
        <div className="text-center py-12 text-muted-foreground" role="alert" data-testid="workflow-schedules-error">
          Failed to load schedules.{' '}
          <button type="button" className="underline" onClick={() => refetch()}>Retry</button>
        </div>
      ) : isLoading ? (
        <div className="text-center py-12 text-muted-foreground" role="status" aria-live="polite" data-testid="workflow-schedules-loading">
          Loading schedules…
        </div>
      ) : schedules.length === 0 ? (
        <div className="rounded-lg border border-dashed py-12 text-center text-muted-foreground" data-testid="workflow-schedules-empty">
          {stateFilter === ALL_STATES
            ? canCreate
              ? 'No schedules yet. Create one to run a workflow on a recurring schedule.'
              : 'No schedules yet.'
            : 'No schedules match this state.'}
        </div>
      ) : (
        <>
          <WorkflowSchedulesTable projectId={projectId!} schedules={schedules} />
          <Pagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            pageSizeOptions={pageSizeOptions}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </>
      )}

      {canCreate && (
        <ScheduleFormDialog
          projectId={projectId!}
          open={createOpen}
          onOpenChange={setCreateOpen}
          onSaved={id => navigate(`/projects/${projectId}/workflows/schedules/${id}`)}
        />
      )}
    </div>
  )
}
